/**
 * Composer attachments — pure logic for the "Merlin box" file affordances.
 *
 * Every Agent-SDK chat composer (Merlin Analyst / Quality, the Tech Support
 * panel, the admin Agent Console) accepts files three ways — drag-and-drop,
 * paste (screenshots), and a picker — through the shared hook
 * (`useComposerAttachments`) + tray (`AttachmentTray`). This module is the
 * side-effect-free core those build on: the accept list (mirrors the backend
 * whitelist in services/agent_console/attachments.py), per-file/size/count
 * screening, and clipboard/drag extraction. Kept in a `.ts` so it is
 * unit-testable and the pages export only components (react-refresh rule).
 */

/** Mirrors ALLOWED_ATTACHMENT_MIME_TYPES on the backend — reject early, client-side. */
export const ACCEPTED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/json',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

/** Extension fallback for files the browser reports with no/generic MIME. */
const EXT_TO_MIME: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/** `accept` attribute for the hidden file input (types + extensions). */
export const ACCEPT_ATTR = [
  ...ACCEPTED_MIME_TYPES,
  ...Object.keys(EXT_TO_MIME).map((e) => `.${e}`),
].join(',');

/** Mirrors MAX_ATTACHMENT_BYTES on the backend. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** Per-message cap so one turn can't stage an unbounded pile. */
export const MAX_FILES_PER_MESSAGE = 5;

/**
 * The MIME type to trust for a file: the browser's when set, else inferred from
 * the extension (Windows often hands CSV/JSON over with an empty type).
 */
export function effectiveMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_TO_MIME[ext] ?? '';
}

export function isImageMime(mime: string): boolean {
  return mime.startsWith('image/');
}

export function formatBytes(size: number): string {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
}

export interface ScreenResult {
  /** The new full staged list (current + admitted files). */
  accepted: File[];
  /** One human-readable line per rejected file. */
  rejected: string[];
}

/**
 * Screen incoming files against the accept list, size cap, and per-message
 * count, merging them onto the currently staged list. Pure.
 */
export function screenFiles(current: File[], incoming: File[]): ScreenResult {
  const accepted = [...current];
  const rejected: string[] = [];
  for (const file of incoming) {
    if (accepted.length >= MAX_FILES_PER_MESSAGE) {
      rejected.push(
        `${file.name}: at most ${MAX_FILES_PER_MESSAGE} files per message.`,
      );
      continue;
    }
    const mime = effectiveMimeType(file);
    if (!ACCEPTED_MIME_TYPES.has(mime)) {
      rejected.push(`${file.name}: unsupported file type.`);
      continue;
    }
    if (file.size <= 0) {
      rejected.push(`${file.name}: file is empty.`);
      continue;
    }
    if (file.size > MAX_FILE_BYTES) {
      rejected.push(`${file.name}: over the 25 MB limit.`);
      continue;
    }
    accepted.push(file);
  }
  return { accepted, rejected };
}

/**
 * Files carried by a paste event's clipboard, if any (a pasted screenshot
 * arrives as an `image/png` File). Text-only pastes return [].
 */
export function clipboardFiles(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (f) out.push(f);
  }
  return out;
}

/** True when a drag carries files (vs. text/element drags, which composers ignore). */
export function dragHasFiles(data: DataTransfer | null): boolean {
  return Array.from(data?.types ?? []).includes('Files');
}
